/** 布局主题预设色（与项目配置抽屉色板一致） */
export const LAYOUT_THEME_PRESETS = [
  '#087dff',
  '#018ffb',
  '#001628',
  '#009688',
  '#536dfe',
  '#ff5c93',
  '#ee4f12',
  '#0096c7',
  '#9c27b0',
  '#ff9800',
  '#ff4d4f',
  '#52c41a',
  '#722ed1'
]

export const LAYOUT_THEME_DEFAULT = '#087dff'

/** 各导航模式下的默认主题色 */
export const LAYOUT_THEME_DEFAULTS = {
  top: {
    system: '#087dff',
    topBar: '#087dff',
    menu: '#087dff'
  },
  sidebar: {
    system: '#087dff',
    topBar: '#018ffb',
    menu: '#001628'
  }
}

const LEGACY_STORAGE_KEYS = {
  system: 'layout_theme_system',
  topBar: 'layout_theme_top_bar',
  menu: 'layout_theme_menu'
}

function resolveNavBarMode(mode) {
  return mode === 'top' ? 'top' : 'sidebar'
}

function storageKey(navBarMode, type) {
  const mode = resolveNavBarMode(navBarMode)
  return `layout_theme_${mode}_${type}`
}

function normalizeHex(color, fallback = LAYOUT_THEME_DEFAULT) {
  if (!color) return fallback
  let hex = String(color).trim().toLowerCase()
  if (!hex.startsWith('#')) hex = `#${hex}`
  if (hex.length === 4) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  }
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : fallback
}

function migrateLegacyThemeStorage() {
  const legacySystem = localStorage.getItem(LEGACY_STORAGE_KEYS.system)
  if (!legacySystem) return
  if (localStorage.getItem(storageKey('top', 'system'))) return

  localStorage.setItem(
    storageKey('top', 'system'),
    normalizeHex(legacySystem, LAYOUT_THEME_DEFAULTS.top.system)
  )
  localStorage.setItem(
    storageKey('top', 'topBar'),
    normalizeHex(
      localStorage.getItem(LEGACY_STORAGE_KEYS.topBar),
      LAYOUT_THEME_DEFAULTS.top.topBar
    )
  )
  localStorage.setItem(
    storageKey('top', 'menu'),
    normalizeHex(localStorage.getItem(LEGACY_STORAGE_KEYS.menu), LAYOUT_THEME_DEFAULTS.top.menu)
  )
}

function parseHex(hex) {
  const normalized = normalizeHex(hex).slice(1)
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  }
}

function rgbToHex(r, g, b) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)))
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`
}

/** percent: 0~100，towardWhite=true 向白色混合 */
function mixColor(hex, percent, towardWhite = true) {
  const { r, g, b } = parseHex(hex)
  const target = towardWhite ? 255 : 0
  const ratio = Math.max(0, Math.min(100, percent)) / 100
  return rgbToHex(
    r + (target - r) * ratio,
    g + (target - g) * ratio,
    b + (target - b) * ratio
  )
}

function hexToRgbString(hex) {
  const { r, g, b } = parseHex(hex)
  return `${r}, ${g}, ${b}`
}

function buildBarGradient(base, direction = '90deg') {
  const light = mixColor(base, 8, true)
  const mid = normalizeHex(base)
  const dark = mixColor(base, 28, false)
  return `linear-gradient(${direction}, ${light} 0%, ${mid} 55%, ${dark} 100%)`
}

export function readLayoutThemeColors(navBarMode = 'sidebar') {
  migrateLegacyThemeStorage()
  const mode = resolveNavBarMode(navBarMode)
  const defaults = LAYOUT_THEME_DEFAULTS[mode]

  const readOne = (type) => {
    const fallback = defaults[type]
    const stored = localStorage.getItem(storageKey(mode, type))
    return normalizeHex(stored, fallback)
  }

  return {
    system: readOne('system'),
    topBar: readOne('topBar'),
    menu: readOne('menu')
  }
}

export function saveLayoutThemeColor(type, color, navBarMode = 'sidebar') {
  const mode = resolveNavBarMode(navBarMode)
  const fallback = LAYOUT_THEME_DEFAULTS[mode][type] || LAYOUT_THEME_DEFAULT
  const normalized = normalizeHex(color, fallback)
  localStorage.setItem(storageKey(mode, type), normalized)
  return normalized
}

export function isPresetThemeColor(color) {
  const normalized = normalizeHex(color)
  return LAYOUT_THEME_PRESETS.some((item) => item === normalized)
}

export function applyLayoutThemeVars(el, themes) {
  if (!el) return
  const system = normalizeHex(themes && themes.system)
  const topBar = normalizeHex(themes && themes.topBar)
  const menu = normalizeHex(themes && themes.menu)
  const systemRgb = hexToRgbString(system)

  el.style.setProperty('--layout-primary', system)
  el.style.setProperty('--layout-primary-rgb', systemRgb)
  el.style.setProperty('--layout-primary-hover', mixColor(system, 12, false))
  el.style.setProperty('--layout-primary-light', mixColor(system, 18, true))
  el.style.setProperty('--layout-primary-dark', mixColor(system, 18, false))
  el.style.setProperty('--layout-primary-soft', `rgba(${systemRgb}, 0.08)`)
  el.style.setProperty('--layout-primary-soft-border', `rgba(${systemRgb}, 0.35)`)
  el.style.setProperty('--layout-primary-active-bg', `rgba(${systemRgb}, 0.06)`)
  el.style.setProperty('--layout-primary-focus-shadow', `rgba(${systemRgb}, 0.2)`)
  el.style.setProperty('--layout-top-bar-bg', buildBarGradient(topBar, '90deg'))
  el.style.setProperty('--layout-menu-bg', buildBarGradient(menu, '180deg'))
  el.style.setProperty('--layout-menu-popup-bg', buildBarGradient(menu, '180deg'))
  el.style.setProperty('--layout-sidebar-toolbar-bg', buildBarGradient(topBar, '90deg'))
  el.style.setProperty(
    '--layout-tools-column-head-bg',
    `linear-gradient(180deg, ${mixColor(topBar, 12, true)} 0%, ${topBar} 100%)`
  )
  el.style.setProperty(
    '--layout-avatar-bg',
    `linear-gradient(135deg, ${mixColor(system, 20, true)}, ${system})`
  )
  el.style.setProperty('--layout-mode-preview-sidebar', mixColor(menu, 28, false))
  el.style.setProperty('--layout-mode-preview-header', mixColor(menu, 28, false))
}

export function readNavBarMode() {
  const mode = localStorage.getItem('layout_nav_bar_mode')
  return mode === 'top' ? 'top' : 'sidebar'
}

/** 将当前模式主题变量写入根节点（Layout 内外、弹层 append-to-body 共用） */
export function initLayoutThemeRoot(navBarMode) {
  const mode = navBarMode || readNavBarMode()
  const themes = readLayoutThemeColors(mode)
  if (typeof document !== 'undefined') {
    applyLayoutThemeVars(document.documentElement, themes)
    const appEl = document.getElementById('app')
    if (appEl) applyLayoutThemeVars(appEl, themes)
    window.dispatchEvent(new CustomEvent('layout-theme-change', { detail: themes }))
  }
  return themes
}

export function getLayoutPrimaryColor() {
  if (typeof document === 'undefined') return LAYOUT_THEME_DEFAULT
  const val = getComputedStyle(document.documentElement).getPropertyValue('--layout-primary').trim()
  return val || LAYOUT_THEME_DEFAULT
}

export function getLayoutPrimaryRgba(alpha = 1) {
  const a = Math.max(0, Math.min(1, Number(alpha) || 0))
  if (typeof document === 'undefined') return `rgba(8, 125, 255, ${a})`
  const rgb = getComputedStyle(document.documentElement).getPropertyValue('--layout-primary-rgb').trim()
  if (!rgb) return `rgba(8, 125, 255, ${a})`
  return `rgba(${rgb}, ${a})`
}
